import React, { useMemo } from 'react';
import * as THREE from 'three';

interface TreeProps {
  position: [number, number, number];
  type?: 'normal' | 'pine' | 'autumn' | 'dead';
}

export const Tree: React.FC<TreeProps> = ({ position, type }) => {
  // Randomly select a tree type if not specified
  const treeType = useMemo(() => {
    if (type) return type;
    const types = ['normal', 'pine', 'autumn', 'dead'];
    const weights = [0.6, 0.25, 0.1, 0.05]; // Higher chance for normal trees

    const random = Math.random();
    let sum = 0;
    for (let i = 0; i < weights.length; i++) {
      sum += weights[i];
      if (random <= sum)
        return types[i] as 'normal' | 'pine' | 'autumn' | 'dead';
    }
    return 'normal';
  }, [type]);

  // Add some random variation to the tree
  const {
    scale,
    trunkHeight,
    trunkRadius,
    trunkSegments,
    leavesHeight,
    leavesRadius,
    leavesSegments,
    trunkColor,
    leavesColor,
  } = useMemo(() => {
    // Base scale with some randomness
    const baseScale = 0.8 + Math.random() * 0.4;

    // Different properties based on tree type
    switch (treeType) {
      case 'pine':
        return {
          scale: baseScale * 1.2,
          trunkHeight: 3 + Math.random() * 1.5,
          trunkRadius: 0.3 + Math.random() * 0.2,
          trunkSegments: 6,
          leavesHeight: 6 + Math.random() * 2,
          leavesRadius: 1.5 + Math.random() * 0.8,
          leavesSegments: 8,
          trunkColor: new THREE.Color('#5D4037').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
          leavesColor: new THREE.Color('#2E7D32').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
        };
      case 'autumn':
        return {
          scale: baseScale * 0.9,
          trunkHeight: 2 + Math.random() * 1,
          trunkRadius: 0.3 + Math.random() * 0.2,
          trunkSegments: 6,
          leavesHeight: 2 + Math.random() * 1,
          leavesRadius: 2 + Math.random() * 1,
          leavesSegments: 6,
          trunkColor: new THREE.Color('#795548').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
          leavesColor: new THREE.Color(
            Math.random() > 0.5 ? '#FF9800' : '#F57F17'
          ).multiplyScalar(0.8 + Math.random() * 0.4),
        };
      case 'dead':
        return {
          scale: baseScale * 0.85,
          trunkHeight: 2 + Math.random() * 1.5,
          trunkRadius: 0.25 + Math.random() * 0.15,
          trunkSegments: 5,
          leavesHeight: 1,
          leavesRadius: 1.5,
          leavesSegments: 4,
          trunkColor: new THREE.Color('#616161').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
          leavesColor: new THREE.Color('#757575').multiplyScalar(
            0.7 + Math.random() * 0.3
          ),
        };
      default: // normal
        return {
          scale: baseScale,
          trunkHeight: 2 + Math.random() * 1,
          trunkRadius: 0.3 + Math.random() * 0.2,
          trunkSegments: 6,
          leavesHeight: 4 + Math.random() * 1,
          leavesRadius: 2 + Math.random() * 0.5,
          leavesSegments: 6,
          trunkColor: new THREE.Color('#795548').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
          leavesColor: new THREE.Color('#4CAF50').multiplyScalar(
            0.8 + Math.random() * 0.4
          ),
        };
    }
  }, [treeType]);

  // Create a unique rotation for each tree
  const rotation = useMemo(() => {
    return [0, Math.random() * Math.PI * 2, 0];
  }, []);

  return (
    <group position={position} scale={scale} rotation={rotation as any}>
      {/* Trunk */}
      <mesh position={[0, trunkHeight / 2, 0]} castShadow>
        <cylinderGeometry
          args={[trunkRadius * 0.7, trunkRadius, trunkHeight, trunkSegments]}
        />
        <meshLambertMaterial color={trunkColor.getHex()} flatShading />
      </mesh>

      {/* Leaves */}
      {treeType === 'pine' ? (
        // Pine tree has multiple cone layers
        <>
          <mesh position={[0, trunkHeight + leavesHeight * 0.25, 0]} castShadow>
            <coneGeometry
              args={[leavesRadius, leavesHeight * 0.5, leavesSegments]}
            />
            <meshLambertMaterial color={leavesColor.getHex()} flatShading />
          </mesh>
          <mesh position={[0, trunkHeight + leavesHeight * 0.5, 0]} castShadow>
            <coneGeometry
              args={[leavesRadius * 0.8, leavesHeight * 0.5, leavesSegments]}
            />
            <meshLambertMaterial color={leavesColor.getHex()} flatShading />
          </mesh>
          <mesh position={[0, trunkHeight + leavesHeight * 0.75, 0]} castShadow>
            <coneGeometry
              args={[leavesRadius * 0.6, leavesHeight * 0.5, leavesSegments]}
            />
            <meshLambertMaterial color={leavesColor.getHex()} flatShading />
          </mesh>
        </>
      ) : treeType === 'dead' ? (
        // Dead tree has just branches, no leaves
        <>
          <mesh
            position={[0.5, trunkHeight, 0]}
            rotation={[0, 0, Math.PI / 4]}
            castShadow
          >
            <cylinderGeometry args={[0.1, 0.2, 1.5, 3]} />
            <meshLambertMaterial color={trunkColor.getHex()} flatShading />
          </mesh>
          <mesh
            position={[-0.5, trunkHeight, 0]}
            rotation={[0, 0, -Math.PI / 4]}
            castShadow
          >
            <cylinderGeometry args={[0.1, 0.2, 1.2, 3]} />
            <meshLambertMaterial color={trunkColor.getHex()} flatShading />
          </mesh>
          <mesh
            position={[0, trunkHeight, 0.5]}
            rotation={[Math.PI / 4, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[0.1, 0.2, 1.3, 3]} />
            <meshLambertMaterial color={trunkColor.getHex()} flatShading />
          </mesh>
        </>
      ) : (
        // Normal and autumn trees have a single leaf mass
        <mesh position={[0, trunkHeight + leavesHeight / 2, 0]} castShadow>
          {treeType === 'autumn' ? (
            <sphereGeometry
              args={[leavesRadius, leavesSegments, leavesSegments]}
            />
          ) : (
            <coneGeometry args={[leavesRadius, leavesHeight, leavesSegments]} />
          )}
          <meshLambertMaterial color={leavesColor.getHex()} flatShading />
        </mesh>
      )}
    </group>
  );
};
